import type {
  MaritimeBroadcastWidgetConfiguration,
  MaritimeForecastWidgetConfiguration,
  MaritimeSeichesWidgetConfiguration,
} from "./generated";
import {
  isMaritimeBroadcastType,
  MaritimeBroadcastType,
} from "./maritime-widget-configuration";

const DEFAULT_BROADCAST_TYPE: MaritimeBroadcastType = "marifoon_nl";

function readRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") return null;
  return value as Record<string, unknown>;
}

function filterAreaIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of raw) {
    if (typeof item !== "string" || item.length === 0 || seen.has(item)) continue;
    seen.add(item);
    result.push(item);
  }
  return result;
}

/** Returns configured area ids in order. */
export function resolveMaritimeForecastAreaIds(
  config: Partial<MaritimeForecastWidgetConfiguration> | null | undefined,
): string[] {
  return filterAreaIds(config?.area_ids);
}

/** Returns configured area id. */
export function resolveMaritimeSeichesAreaId(
  config: Partial<MaritimeSeichesWidgetConfiguration> | null | undefined,
): string {
  const areaId = config?.area_id;
  return typeof areaId === "string" ? areaId : "";
}

export function normalizeMaritimeSeichesConfiguration(
  partial: unknown,
): MaritimeSeichesWidgetConfiguration {
  const c = readRecord(partial);
  const areaId = c?.area_id;
  return {
    type: "maritime_seiches",
    area_id: typeof areaId === "string" ? areaId : "",
  } as MaritimeSeichesWidgetConfiguration;
}

export function normalizeMaritimeBroadcastConfiguration(
  partial: unknown,
): MaritimeBroadcastWidgetConfiguration {
  const c = readRecord(partial);
  const broadcastType = c?.broadcast_type;
  return {
    type: "maritime_broadcast",
    broadcast_type:
      typeof broadcastType === "string" && isMaritimeBroadcastType(broadcastType)
        ? broadcastType
        : DEFAULT_BROADCAST_TYPE,
  } as MaritimeBroadcastWidgetConfiguration;
}

export function normalizeMaritimeForecastConfiguration(
  partial: unknown,
): MaritimeForecastWidgetConfiguration {
  const c = readRecord(partial);
  return {
    type: "maritime_forecast",
    area_ids: filterAreaIds(c?.area_ids),
  } as MaritimeForecastWidgetConfiguration;
}

/** Merge partial form/API payload onto existing forecast configuration. */
export function mergeMaritimeForecastConfiguration(
  existing: unknown,
  partial: unknown,
): MaritimeForecastWidgetConfiguration {
  const base = normalizeMaritimeForecastConfiguration(existing);
  const patch = readRecord(partial);
  if (!patch || !("area_ids" in patch)) return base;
  return {
    type: "maritime_forecast",
    area_ids: filterAreaIds(patch.area_ids),
  } as MaritimeForecastWidgetConfiguration;
}

/** Merge partial form/API payload onto existing broadcast configuration. */
export function mergeMaritimeBroadcastConfiguration(
  existing: unknown,
  partial: unknown,
): MaritimeBroadcastWidgetConfiguration {
  const base = normalizeMaritimeBroadcastConfiguration(existing);
  const patch = readRecord(partial);
  if (!patch || !("broadcast_type" in patch)) return base;
  const broadcastType = patch.broadcast_type;
  return {
    type: "maritime_broadcast",
    broadcast_type:
      typeof broadcastType === "string" && isMaritimeBroadcastType(broadcastType)
        ? broadcastType
        : base.broadcast_type,
  } as MaritimeBroadcastWidgetConfiguration;
}

export function isMaritimeWidgetConfigurationType(type: unknown): boolean {
  return (
    type === "maritime_broadcast" ||
    type === "maritime_forecast" ||
    type === "maritime_seiches"
  );
}

/** Normalizes maritime widget configuration on CMS save (non-maritime values pass through). */
export function normalizeMaritimeWidgetFormValues<T extends { configuration?: unknown }>(
  values: T,
): T {
  const config = readRecord(values.configuration);
  if (!config || !isMaritimeWidgetConfigurationType(config.type)) {
    return values;
  }

  if (config.type === "maritime_forecast") {
    const configuration = normalizeMaritimeForecastConfiguration(config);
    return { ...values, configuration } as T;
  }

  if (config.type === "maritime_seiches") {
    const configuration = normalizeMaritimeSeichesConfiguration(config);
    return { ...values, configuration } as T;
  }

  const configuration = normalizeMaritimeBroadcastConfiguration(config);
  return { ...values, configuration } as T;
}
